import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCustomerDtoToCustomer } from './customer.converters';
import {
    convertAttachmentToAttachmentDto,
    convertItemsToActionItemsDto,
} from '../common.converters';
import {
    CreateCustomerReqDto,
    CreateCustomerResDto,
    CustomersService,
    GetCustomer1ResDto,
    GetCustomerResDto,
    type UpdateCustomerReqDto,
    UpdateCustomerResDto,
} from '../../generated';
import { CustomMutationOptions, CustomQueryOptions } from '../common.types';
import { Attachment, Customer } from './customer.types';

interface UseCustomersData {
    name?: string;
    email?: string;
    phoneNumber?: string;
    pageNumber?: number;
    pageSize?: number;
}

export const useUpdateCustomerAttachments = (
    oldItems: Attachment[],
    options: CustomMutationOptions<
        {
            customerId: string;
            items: Attachment[];
        },
        UpdateCustomerResDto | null
    > = {}
) => {
    return useMutation({
        mutationFn: async ({ customerId, items }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems.map(convertAttachmentToAttachmentDto),
                items.map(convertAttachmentToAttachmentDto),
                'id',
                (oldItem, newItem) => oldItem.id !== newItem.id
            );

            if (requestItems.length) {
                const response =
                    await CustomersService.updateAttachmentsCustomers({
                        path: { customer_id: customerId },
                        body: {
                            items: requestItems,
                        },
                    });

                if (!response.data)
                    throw new Error('Failed to update customer attachments');

                return response.data;
            }
            return null;
        },
        ...options,
    });
};

const useCustomerDebounceKey = 'useCustomerDebounceKey';
export const useDebouncedCustomers = (
    search: string,
    options?: CustomQueryOptions<GetCustomerResDto>
) => {
    const [params, setParams] = React.useState<UseCustomersData | undefined>();

    React.useEffect(() => {
        const handler = setTimeout(() => {
            const filter: UseCustomersData = {};
            if (search.match(/^\+?[0-9 ]+$/)) filter.phoneNumber = search;
            else if (search.match(/^[^@]+@[^@.]+\.[a-zA-Z]+$/))
                filter.email = search;
            else if (search.length > 2) {
                filter.name = search;
            }
            setParams(Object.keys(filter).length ? filter : undefined);
        }, 1000);

        return () => {
            clearTimeout(handler);
        };
    }, [search]);

    const { data, ...other } = useQuery({
        queryKey: [useCustomerDebounceKey, params],
        queryFn: async () => {
            const response = await CustomersService.getCustomer({
                query: {
                    ...(params?.name && { name: params.name }),
                    ...(params?.email && { email: params.email }),
                    ...(params?.phoneNumber && {
                        phone_number: params.phoneNumber,
                    }),
                },
            });
            if (!response.data) throw new Error('Failed');

            return response.data;
        },
        keepPreviousData: false,
        staleTime: 0,
        enabled: !!params,
        ...options,
    });

    const parsedData = useMemo(
        () =>
            params && data?.items
                ? data?.items.map(getCustomerDtoToCustomer)
                : undefined,
        [params, data]
    );

    return {
        data: parsedData,
        ...other,
        isLoading: other.isFetching,
    };
};

export const useAddCustomer = (
    options?: CustomMutationOptions<
        {
            customer: CreateCustomerReqDto;
            attachments: Attachment[];
        },
        CreateCustomerResDto
    >
) => {
    const { mutateAsync: updateAttachments } = useUpdateCustomerAttachments([]);

    return useMutation({
        mutationFn: async ({ customer, attachments }) => {
            const response = await CustomersService.createCustomer({
                body: customer,
            });

            if (!response.data) throw new Error('Failed to create customer');

            if (attachments.length > 0) {
                await updateAttachments(
                    {
                        customerId: response.data.id,
                        items: attachments,
                    },
                    {
                        onError: () => {
                            throw new Error('Failed to upload file', {
                                cause: 'attachment',
                            });
                        },
                    }
                );
            }
            return response.data;
        },
        ...options,
    });
};

const useCustomerKey = 'useCustomerKey';

export const useUpdateCustomer = (
    customer?: Customer,
    options?: CustomMutationOptions<{
        id: string;
        customer?: UpdateCustomerReqDto;
        attachments?: Attachment[];
    }>
) => {
    const queryClient = useQueryClient();

    const { mutateAsync: updateAttachments } = useUpdateCustomerAttachments(
        customer?.attachments || []
    );

    return useMutation({
        mutationFn: async ({ id, customer: newCustomer, attachments = [] }) => {
            let response: Partial<
                Omit<GetCustomer1ResDto | UpdateCustomerResDto, 'rbac'>
            > | null;

            response = await updateAttachments(
                {
                    customerId: id,
                    items: attachments,
                },
                {
                    onError: () => {
                        throw new Error('Failed to upload file', {
                            cause: 'attachment',
                        });
                    },
                }
            );

            try {
                if (newCustomer) {
                    const updateResponse =
                        await CustomersService.updateCustomer({
                            path: { customer_id: id },
                            body: newCustomer,
                        });

                    if (!updateResponse.data)
                        throw new Error('Failed to update customer');

                    response = updateResponse.data;
                }
            } catch (e) {
                const getResponse = await CustomersService.getCustomer1({
                    path: { customer_id: id },
                });

                if (!getResponse.data)
                    throw new Error('Failed to get customer');

                response = getResponse.data;
            }

            if (!response) {
                const getResponse = await CustomersService.getCustomer1({
                    path: { customer_id: id },
                });

                if (!getResponse.data)
                    throw new Error('Failed to get customer');

                response = getResponse.data;
            }

            queryClient.setQueryData<GetCustomer1ResDto>(
                [useCustomerKey, id],
                // @ts-ignore
                prevCustomer =>
                    prevCustomer
                        ? {
                              ...prevCustomer,
                              ...response,
                          }
                        : undefined
            );

            return response;
        },
        ...options,
    });
};

export const useCustomer = (
    id?: string,
    options: CustomQueryOptions<GetCustomer1ResDto> = {}
) => {
    const { data, ...rest } = useQuery({
        queryKey: [useCustomerKey, id],
        queryFn: async () => {
            const response = await CustomersService.getCustomer1({
                path: { customer_id: id as string },
            });

            if (!response.data) throw new Error('Failed to get customer');

            return response.data;
        },
        enabled: !!id,
        ...options,
    });

    const parsedData = useMemo(
        () => (data ? getCustomerDtoToCustomer(data) : undefined),
        [data]
    );

    return {
        data: parsedData,
        ...rest,
    };
};

export const useCreateCustomer = (
    options?: CustomMutationOptions<CreateCustomerReqDto, CreateCustomerResDto>
) => {
    return useMutation({
        mutationFn: async requestBody => {
            const response = await CustomersService.createCustomer({
                body: requestBody,
            });

            if (!response.data) throw new Error('Failed to create customer');

            return response.data;
        },
        ...options,
    });
};
